import { useEffect, useState, type FC } from "react";
import { StepTitleDescription } from "../StepTitleDescritpion";
import { GlobalPrescriptionTable } from "@/components/GlobalPrescriptionTable";
import Accordion from "../UI/Accordion";
import type { AccordionItemProps } from "../UI/Accordion";
import {
  Table,
  type TableColumn,
  type TableRow,
} from "@/components/UI/Table/Table";
import { PrescriptionData } from "@/types/prescription.types";
import { useStore } from "@/store";
import { base64FileToImage, base64IsImage } from "@/utils/base64";
import { downloadPrescriptionFile } from "@/services/downloadPrescription";
import { useTranslation } from "@/i18n";
import styles from "./PrescriptionDetailsModal.module.css";
import "@styles/typography.css";
import prescriptionDefaultImage from "@/assets/images/prescriptionDefaultImage.svg";
import { getContactLensById } from "@/services/getContactLensById";

interface PrescriptionDetailsModalProps {
  prescriptionData: PrescriptionData;
}

export const PrescriptionDetailsModal: FC<PrescriptionDetailsModalProps> = ({
  prescriptionData,
}) => {
  const config = useStore().config;

  const [prescriptionImage, setPrescriptionImage] = useState<string | null>(
    null,
  );
  const { t } = useTranslation();

  const [rightPrescriptionData, setRightPrescriptionData] = useState<any>(null);
  const [leftPrescriptionData, setLeftPrescriptionData] = useState<any>(null);

  useEffect(() => {
    if (config) {
      const getLensesData = async () => {
        try {
          const rightData = await getContactLensById(
            config.storeId,
            config.catalogId,
            prescriptionData.rightCatentryId.toString(),
            config.basePath,
          );
          const leftData = await getContactLensById(
            config.storeId,
            config.catalogId,
            prescriptionData.leftCatentryId.toString(),
            config.basePath,
          );
          setRightPrescriptionData({ product: rightData.catalogEntry });
          setLeftPrescriptionData({ product: leftData.catalogEntry });
        } catch (error) {
          throw new Error(
            "Error retrieving lens data from prescription: " + error,
          );
        }
      };
      getLensesData();
    }
  }, [config, prescriptionData]);

  const doctorColumns: TableColumn[] = [
    {
      header: t(
        "modal.prescriptionDetails.accordions.doctorDetails.doctorName",
      ),
      key: "doctor",
    },
    {
      header: t(
        "modal.prescriptionDetails.accordions.doctorDetails.practiceName",
      ),
      key: "practice",
    },
    {
      header: t("modal.prescriptionDetails.accordions.doctorDetails.address"),
      key: "address",
    },
  ];

  const doctorRows: TableRow[] = [
    {
      doctor: prescriptionData.doctor || "—",
      practice: prescriptionData.clinic || "—",
      address: (
        <>
          {prescriptionData.doctorAddress}
          <br />
          {prescriptionData.doctorState}
          <br />
          <br />
          <span className="textSm bold underline">
            {prescriptionData.doctorFax}
          </span>
        </>
      ),
    },
  ];

  // Helper: checks if all values in an object are non-empty
  const hasAnyData = (fields: (string | null | undefined)[]) =>
    fields.some((f) => f != null && f !== "");

  const accordionItems: AccordionItemProps[] = [
    {
      value: "prescriptionValues",
      trigger: t(
        "modal.prescriptionDetails.accordions.prescriptionValues.title",
      ),
      // prescriptionValues always shows — it comes from the store,
      // add a guard here if you need one too
      children: (
        <div className={styles.tableWrapper}>
          <GlobalPrescriptionTable
            contactLensesData={{
              OD: rightPrescriptionData,
              OS: leftPrescriptionData,
            }}
            disableRemove={true}
            modal={true}
            isEditable={false}
          />
        </div>
      ),
    },
    hasAnyData([
      prescriptionData.doctor,
      prescriptionData.clinic,
      prescriptionData.doctorAddress,
      prescriptionData.doctorState,
      prescriptionData.doctorFax,
    ]) && {
      value: "doctorDetails",
      trigger: t("modal.prescriptionDetails.accordions.doctorDetails.title"),
      children: (
        <div className={styles.tableWrapper}>
          <Table
            columns={doctorColumns}
            rows={doctorRows}
            highlightedColumn={null}
            firstColumnHeader={false}
            stackedMobile={true}
          />
        </div>
      ),
    },
    hasAnyData([prescriptionData.fileName]) && {
      value: "submissionMethod",
      trigger: t("modal.prescriptionDetails.accordions.submissionMethod.title"),
      children: (
        <div className={styles.methodContent}>
          <div className={styles.submissionCard}>
            <div className={styles.submissionImageWrapper}>
              {!!prescriptionImage ? (
                <img
                  className="prescription-preview-image"
                  src={prescriptionImage}
                />
              ) : (
                <img
                  className="prescription-preview-image"
                  src={prescriptionDefaultImage}
                  alt="Default Prescription Image"
                />
              )}
            </div>
            <div className={styles.submissionText}>
              {prescriptionData.fileName}
            </div>
          </div>
        </div>
      ),
    },
  ].filter(Boolean) as AccordionItemProps[];

  useEffect(() => {
    if (prescriptionData && prescriptionData.fileName && config) {
      downloadPrescriptionFile(
        config.storeId,
        prescriptionData.fileName,
        config.basePath,
      ).then((response) => {
        if (!!response?.prescriptionFile) {
          if (base64IsImage(response.prescriptionFile)) {
            setPrescriptionImage(base64FileToImage(response.prescriptionFile));
          }
        }
      });
    }
  }, [prescriptionData]);

  return (
    <div className={styles.container}>
      <StepTitleDescription
        title={t("modal.prescriptionDetails.title")}
        description={t("modal.prescriptionDetails.description")}
      />

      <Accordion
        type="multiple"
        defaultValue={[
          "prescriptionValues",
          "doctorDetails",
          "prescriptionImage",
        ]}
        items={accordionItems}
        className={styles.accordionRoot}
      />
    </div>
  );
};
